import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router';
import { IconeDocumento, IconeLista, IconeRelogio, IconeSelo } from '../components/Icones.js';
import { SemCadastro } from '../components/SemCadastro.js';
import {
  Abas,
  Aguardar,
  BotaoLink,
  CabecalhoPagina,
  Etiqueta,
  EstadoVazio,
  FaixaIndicadores,
  Indicador,
  Lista,
  type ColunaLista,
} from '../components/ui/index.js';
import { formatarData } from '../dados/catalogo.js';
import { useConsulta, useFonte, usePodeCadastrar } from '../dados/contexto.js';
import type { Animal, Avaliacao, Visita } from '../dados/fonte.js';

export type Situacao = 'planejada' | 'andamento' | 'fechada';

/** Situacao da visita, derivada das avaliacoes (nunca gravada). */
export function situacaoDaVisita(avaliacoes: readonly Avaliacao[], visita?: { closed_at: string | null }): Situacao {
  if (visita?.closed_at) return 'fechada';
  if (avaliacoes.length > 0 && avaliacoes.every((a) => a.status === 'fechada' || a.status === 'validada'))
    return 'fechada';
  if (avaliacoes.every((a) => a.status === 'rascunho' && !a.started_at)) return 'planejada';
  return 'andamento';
}

export const ROTULO_SITUACAO: Record<Situacao, string> = {
  planejada: 'Planejada',
  andamento: 'Em andamento',
  fechada: 'Fechada',
};

const SITUACOES: Situacao[] = ['planejada', 'andamento', 'fechada'];
const PLURAL: Record<Situacao, string> = { planejada: 'Planejadas', andamento: 'Em andamento', fechada: 'Fechadas' };
const fechada = (a: Avaliacao) => a.status === 'fechada' || a.status === 'validada';
type OrdemVisitas = 'numero' | 'animais' | 'fechadas';

interface LinhaVisita {
  visita: Visita;
  fichas: Avaliacao[];
  situacao: Situacao;
  completos: number;
  fechadas: number;
}

/** Visitas do haras atual: resumo, abas por situacao e a lista que abre nas fichas. */
export function Visitas() {
  const fonte = useFonte();
  const equipe = usePodeCadastrar();
  const consulta = useConsulta(async (f) => {
    const [haras, visitas, avaliacoes, animais] = await Promise.all([
      f.haras(),
      f.cadastro ? f.cadastro.visitas() : Promise.resolve([]),
      f.avaliacoes(),
      f.listarAnimais(),
    ]);
    return { haras, visitas, avaliacoes, animais };
  }, []);

  if (!fonte.cadastro || !equipe) return <SemCadastro demo={fonte.ficticia} />;

  return (
    <Aguardar consulta={consulta}>
      {({ haras, visitas, avaliacoes, animais }) => (
        <div className="space-y-6">
          <CabecalhoPagina
            sobretitulo={haras?.name}
            titulo="Visitas"
            meta={<span>Planeje quais animais serão avaliados em cada visita ao haras.</span>}
            acoes={
              haras && (
                <BotaoLink variante="principal" to="/visitas/nova">
                  Nova visita
                </BotaoLink>
              )
            }
          />
          {!haras ? (
            <EstadoVazio
              titulo="Nenhum haras cadastrado"
              acao={
                <BotaoLink variante="principal" to="/haras/novo">
                  Cadastrar haras
                </BotaoLink>
              }
            />
          ) : visitas.length === 0 ? (
            <EstadoVazio
              titulo="Nenhuma visita planejada"
              acao={
                <BotaoLink variante="principal" to="/visitas/nova">
                  Planejar a primeira visita
                </BotaoLink>
              }
            >
              A pré-visita define os animais e o nível de avaliação de cada um, e deixa tudo no tablet para o dia.
            </EstadoVazio>
          ) : (
            <ListaDeVisitas visitas={visitas} avaliacoes={avaliacoes} animais={animais} />
          )}
        </div>
      )}
    </Aguardar>
  );
}

function ListaDeVisitas({
  visitas,
  avaliacoes,
  animais,
}: {
  visitas: Visita[];
  avaliacoes: Avaliacao[];
  animais: Animal[];
}) {
  const [params, setParams] = useSearchParams();
  const filtro = SITUACOES.find((s) => s === params.get('situacao')) ?? null;
  const ordem: OrdemVisitas =
    (['numero', 'animais', 'fechadas'] as const).find((o) => o === params.get('ordem')) ?? 'numero';
  const direcao = params.get('dir') === 'asc' ? 'asc' : 'desc';
  const nome = useMemo(() => new Map(animais.map((a) => [a.id, a.name])), [animais]);

  const linhas = useMemo<LinhaVisita[]>(
    () =>
      visitas.map((v) => {
        const fichas = avaliacoes.filter((a) => a.visit_id === v.id);
        return {
          visita: v,
          fichas,
          situacao: situacaoDaVisita(fichas, v),
          completos: fichas.filter((a) => a.protocol_level >= 2).length,
          fechadas: fichas.filter(fechada).length,
        };
      }),
    [visitas, avaliacoes],
  );
  const s = direcao === 'asc' ? 1 : -1;
  const visiveis = linhas
    .filter((l) => !filtro || l.situacao === filtro)
    .sort((a, b) => {
      const n = a.visita.visit_number - b.visita.visit_number;
      if (ordem === 'animais') return s * (a.fichas.length - b.fichas.length) || -n;
      if (ordem === 'fechadas') return s * (taxa(a) - taxa(b)) || -n;
      return s * n;
    });
  const avaliados = new Set(avaliacoes.filter(fechada).map((a) => a.animal_id)).size;
  const conta = (sit: Situacao) => linhas.filter((l) => l.situacao === sit).length;
  const ultimaFechada = linhas
    .filter((l) => l.situacao === 'fechada')
    .map((l) => l.visita.visit_date)
    .sort()
    .at(-1);

  const mudar = (novos: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(novos)) {
      if (v) p.set(k, v);
      else p.delete(k);
    }
    setParams(p, { replace: true, preventScrollReset: true });
  };

  const colunas: ColunaLista<LinhaVisita>[] = [
    {
      id: 'animais',
      rotulo: 'Animais',
      largura: '90px',
      celula: (l) => l.fichas.length,
      medida: (l) => l.fichas.length,
    },
    { id: 'completos', rotulo: 'Completos', largura: '96px', ordenavel: false, celula: (l) => l.completos },
    {
      id: 'fechadas',
      rotulo: 'Fichas fechadas',
      rotuloCurto: 'Fechadas',
      largura: '120px',
      celula: (l) => `${l.fechadas} de ${l.fichas.length}`,
      medida: taxa,
    },
  ];

  return (
    <>
      <Abas
        rotulo="Situação"
        valor={filtro ?? 'todas'}
        onMudar={(v) => mudar({ situacao: v === 'todas' ? null : v })}
        abas={[
          { valor: 'todas', rotulo: 'Todas', contagem: linhas.length },
          ...SITUACOES.map((sit) => ({ valor: sit, rotulo: PLURAL[sit], contagem: conta(sit) })),
        ]}
      />

      <FaixaIndicadores rotulo="Resumo das visitas">
        <Indicador
          destaque
          rotulo="Visitas em andamento"
          valor={conta('andamento')}
          icone={<IconeRelogio width={17} height={17} />}
          complemento={`${conta('planejada')} planejada(s) para os próximos dias`}
        />
        <Indicador
          rotulo="Planejadas"
          valor={conta('planejada')}
          icone={<IconeDocumento width={17} height={17} />}
          complemento="sem coleta iniciada"
        />
        <Indicador
          rotulo="Fechadas"
          valor={conta('fechada')}
          icone={<IconeSelo width={17} height={17} />}
          complemento={ultimaFechada ? `a última em ${formatarData(ultimaFechada)}` : 'nenhuma ainda'}
        />
        <Indicador
          rotulo="Animais avaliados"
          valor={avaliados}
          icone={<IconeLista width={17} height={17} />}
          complemento="com ao menos uma ficha fechada"
        />
      </FaixaIndicadores>

      <section aria-label="Lista de visitas" className="space-y-4">
        <Lista<LinhaVisita>
          key={params.toString()}
          titulo="Visitas"
          dica="Abra uma linha para ver as fichas da visita"
          linhas={visiveis}
          chave={(l) => l.visita.id}
          rotuloItem={(l) => `visita nº ${l.visita.visit_number}`}
          rotuloPrincipal="Visita"
          ordemPrincipal={{ id: 'numero', rotulo: 'Número', texto: false, dirPadrao: 'desc' }}
          colunas={colunas}
          ordem={{ col: ordem, dir: direcao }}
          onOrdenar={(o) => mudar({ ordem: o.col === 'numero' ? null : o.col, dir: o.dir === 'asc' ? 'asc' : null })}
          vazio={filtro ? `Nenhuma visita ${ROTULO_SITUACAO[filtro].toLocaleLowerCase('pt-BR')}.` : 'Nenhuma visita.'}
          principal={(l) => (
            <div className="min-w-0">
              <Link
                to={`/visitas/${l.visita.id}`}
                className="alvo-toque font-serif text-[15px] font-semibold text-marca hover:underline"
              >
                Visita nº {l.visita.visit_number}
              </Link>
              <p className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-apagado">
                <Etiqueta tom={l.situacao === 'planejada' ? 'destaque' : l.situacao === 'fechada' ? 'ok' : 'aviso'}>
                  {ROTULO_SITUACAO[l.situacao]}
                </Etiqueta>
                <span aria-hidden className="opacity-50">
                  ·
                </span>
                <span className="numero">{formatarData(l.visita.visit_date)}</span>
                {l.visita.purpose === 'estudo_repetibilidade' && (
                  <>
                    <span aria-hidden className="opacity-50">
                      ·
                    </span>
                    estudo de repetibilidade
                  </>
                )}
              </p>
            </div>
          )}
          detalhe={(l) => (
            <div className="space-y-3.5">
              {l.fichas.length === 0 ? (
                <p className="text-sm text-apagado">Nenhum animal planejado nesta visita.</p>
              ) : (
                <ul className="grid gap-x-6 gap-y-1.5 text-[13px] sm:grid-cols-2 lg:grid-cols-3">
                  {l.fichas.map((a) => (
                    <li key={a.id} className="flex items-center justify-between gap-3">
                      <span className="min-w-0 truncate">{nome.get(a.animal_id) ?? '—'}</span>
                      <EtiquetaDaFicha a={a} />
                    </li>
                  ))}
                </ul>
              )}
              <div className="flex flex-wrap gap-2 border-t border-borda pt-3.5">
                <BotaoLink tamanho="sm" variante="principal" to={`/visitas/${l.visita.id}`}>
                  Abrir a visita
                </BotaoLink>
                <BotaoLink tamanho="sm" to={`/dados?visita=${l.visita.id}`}>
                  Ver na consulta
                </BotaoLink>
              </div>
            </div>
          )}
        />
      </section>
    </>
  );
}

/** Fracao de fichas fechadas (0 sem fichas): a barrinha e a ordem por "Fichas fechadas". */
function taxa(l: LinhaVisita): number {
  return l.fichas.length ? l.fechadas / l.fichas.length : 0;
}

function EtiquetaDaFicha({ a }: { a: Avaliacao }) {
  if (a.status === 'validada') return <Etiqueta tom="ok">Validada</Etiqueta>;
  if (a.status === 'fechada') return <Etiqueta tom="ok">Fechada</Etiqueta>;
  if (a.status === 'reaberta') return <Etiqueta tom="aviso">Reaberta</Etiqueta>;
  if (a.started_at) return <Etiqueta tom="aviso">Em coleta</Etiqueta>;
  return <Etiqueta>Planejada</Etiqueta>;
}
